import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UserCheck, Loader2, CheckCircle2, XCircle, History, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { VerificationBadge } from './PushedDayLocks';

const API = `${window.__BACKEND_URL__}/api`;

// Preflight: does every worker with time in the period match a QuickBooks employee? Map the misses inline.
export const EmployeePreflight = ({ token, startDate, endDate, onMapped }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState('');
  const run = async () => {
    setBusy(true);
    try { setRes((await axios.post(`${API}/qb/employee-check`, { start_date: startDate, end_date: endDate }, auth)).data); }
    catch (e) { toast({ title: 'Check failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const map = async (row, qbId) => {
    setMapping(row.worker_id);
    try {
      await axios.post(`${API}/qb/employee-map`, { user_id: row.worker_id, qb_employee_id: qbId && qbId !== '__clear' ? qbId : null }, auth);
      toast({ title: qbId && qbId !== '__clear' ? `${row.worker_name} mapped` : 'Mapping cleared' });
      await run(); onMapped?.();
    } catch (e) { toast({ title: 'Mapping failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setMapping(''); }
  };
  const exempt = async (row) => {
    setMapping(row.worker_id);
    try {
      await axios.put(`${API}/users/${row.worker_id}/edit`, { qb_payroll_exempt: true }, auth);
      toast({ title: `${row.worker_name} marked not on QuickBooks payroll`, description: 'Their hours stay in HinesTime but will never be pushed. Undo under Manage Users → Edit.' });
      await run(); onMapped?.();
    } catch (e) { toast({ title: 'Could not mark exempt', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setMapping(''); }
  };
  return (
    <div className="space-y-2" data-testid="pw-preflight">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="pw-check-employees">{busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserCheck className="w-3 h-3 mr-1" />} Check employees</Button>
        {res && <span className={`text-xs ${res.unmatched ? 'text-red-300' : 'text-emerald-300'}`} data-testid="pw-preflight-summary">{res.unmatched ? `${res.unmatched} of ${res.checked} workers don't match a QuickBooks employee` : `All ${res.checked} workers match QuickBooks`}</span>}
        {res?.exempt?.length > 0 && <span className="text-xs text-sky-300" data-testid="pw-preflight-exempt" title={res.exempt.map((x) => x.worker_name).join(', ')}>· {res.exempt.length} exempt (not on QB payroll) skipped</span>}
      </div>
      {res && res.rows.length > 0 && (
        <div className="max-h-56 overflow-auto rounded border border-[#2D3748] text-xs" data-testid="pw-preflight-rows">
          {res.rows.map((r) => (
            <div key={r.worker_id} className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-[#2D3748]/60" data-testid={`pw-preflight-row-${r.worker_id}`}>
              {r.matched ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
              <span className="text-white">{r.worker_name}</span>
              {r.name_mismatch && <span className="text-amber-300">display name out of date — fix under Staff</span>}
              {r.matched ? <span className="text-slate-400">→ {r.qb_employee?.display_name}{r.mapped ? ' (mapped)' : ''}{r.qb_employee && r.qb_employee.active === false ? ' · INACTIVE in QBO' : ''}</span>
                : <span className="text-red-300 truncate max-w-md" title={r.hint}>{r.hint || 'not found in QuickBooks'}</span>}
              <select value="" onChange={(e) => map(r, e.target.value)} disabled={mapping === r.worker_id} className="ml-auto h-7 rounded border border-[#2D3748] bg-[#0B1321] text-slate-200 px-1" data-testid={`pw-preflight-map-${r.worker_id}`}>
                <option value="">{r.mapped ? 'Re-map…' : 'Map to…'}</option>
                {r.mapped && <option value="__clear">Clear mapping</option>}
                {res.qb_employees.map((q) => <option key={q.id} value={q.id}>{q.display_name}{q.active === false ? ' (inactive)' : ''}</option>)}
              </select>
              {!r.matched && (
                <button type="button" onClick={() => exempt(r)} disabled={mapping === r.worker_id} className="h-7 px-2 rounded border border-sky-700/50 text-sky-300 hover:bg-sky-500/10 text-[11px]"
                  title="Not paid through QuickBooks (test account, contractor, owner) — skip them in every push and readiness check" data-testid={`pw-preflight-exempt-${r.worker_id}`}>
                  Mark exempt
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

// Push history: every payroll push with who / when / counts, re-open its per-worker results.
export const PushHistory = ({ token, DetailsTable }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/qb/payroll-syncs`, auth).then((r) => setList(r.data)).catch(() => setList([])); }, [token]);
  if (open) {
    return (
      <div className="space-y-2" data-testid="pw-history-detail">
        <Button size="sm" variant="ghost" onClick={() => setOpen(null)} data-testid="pw-history-back"><ArrowLeft className="w-3 h-3 mr-1" /> All pushes</Button>
        <p className="text-xs text-slate-300 flex flex-wrap items-center gap-2"><span>{open.start_date} → {open.end_date} · {fmt(open.synced_at)} · by {open.pushed_by || 'unknown'} · {open.entries_pushed} pushed · {open.errors} errors{open.already_pushed_skipped ? ` · ${open.already_pushed_skipped} duplicates skipped` : ''}</span><VerificationBadge v={open.verification} /></p>
        {open.details?.length ? <DetailsTable details={open.details} /> : <p className="text-xs text-slate-500">No per-worker details were stored for this push.</p>}
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="pw-history">
      <p className="text-xs font-semibold text-slate-300 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Push history</p>
      {!list ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : list.length === 0 ? <p className="text-xs text-slate-500" data-testid="pw-history-empty">No payroll pushes yet.</p> : (
        <div className="max-h-56 overflow-auto rounded border border-[#2D3748] text-xs">
          {list.map((s) => (
            <button key={s.id} type="button" onClick={async () => setOpen((await axios.get(`${API}/qb/payroll-syncs/${s.id}`, auth)).data)} className="w-full text-left flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-[#2D3748]/60 hover:bg-white/5" data-testid={`pw-history-row-${s.id}`}>
              <span className="text-white">{s.start_date} → {s.end_date}</span>
              <span className="text-slate-400">{fmt(s.synced_at)}</span>
              <span className="text-slate-400">by {s.pushed_by || '—'}</span>
              <span className="ml-auto text-emerald-300">{s.entries_pushed} pushed</span>
              <span className={s.errors ? 'text-red-300' : 'text-slate-500'}>{s.errors} errors</span>
              <VerificationBadge v={s.verification} compact />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
